import React, { FC } from 'react';
import styled from 'styled-components';
import { TranslationHelpers } from '../../../i18n/translationHelpers';
import { COLORS } from '../constants/styles';

const ItemContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
`;

const ProviderIcon = styled.div`
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    img {
        max-width: 100%;
        max-height: 100%;
    }
`;

const ProviderInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`;

const ProviderName = styled.span`
    color: ${COLORS.dark};
    font-size: 0.875rem;
`;

const UnlinkButton = styled.button`
    background: none;
    border: none;
    color: ${COLORS.rakutenRed};
    font-size: 0.875rem;
    cursor: pointer;
    text-decoration: underline;
    padding: 0;
    text-align: left;

    &:hover {
        font-weight: 500;
    }

    &:disabled {
        color: ${COLORS.grey3};
        cursor: not-allowed;
        text-decoration: none;
    }
`;

export interface SocialProviderItemProps {
    providerName: string;
    providerKey: string;
    iconSrc: string;
    onUnlink: () => void;
    disabled?: boolean;
}

/**
 * Shared component for rendering a social provider item (icon, name, unlink button)
 * Used in both EU (Privacy Center) and AP (Social Accounts page) regions
 */
const SocialProviderItem: FC<SocialProviderItemProps> = ({
    providerName,
    providerKey,
    iconSrc,
    onUnlink,
    disabled = false,
}) => {
    return (
        <ItemContainer data-qa-id={`social-provider-${providerKey}`}>
            <ProviderIcon>
                <img src={iconSrc} alt={providerName} />
            </ProviderIcon>
            <ProviderInfo>
                <ProviderName>
                    {TranslationHelpers.getFormattedMessage(
                        `social.provider.${providerKey}.linked`,
                        { provider: providerName },
                    )}
                </ProviderName>
                <UnlinkButton
                    onClick={onUnlink}
                    disabled={disabled}
                    data-qa-id={`unlink-${providerKey}-button`}
                >
                    {TranslationHelpers.getFormattedMessage('social.accounts.unlink')}
                </UnlinkButton>
            </ProviderInfo>
        </ItemContainer>
    );
};

export default SocialProviderItem;
